import Divider from '@mui/material/Divider';
import {
    SOIL_TYPE_OPTIONS,
    CONSISTENCY_OPTIONS,
    BOUNDARY_CLARITY_OPTIONS,
    BOUNDARY_BASIS_OPTIONS,
    CONTOUR_OPTIONS,
    HUMUS_CONTENT_OPTIONS
} from '@ast-data/shared';
import {
    bindFormFields,
    type FormFieldBinding
} from '../../../components/formFields';
import FormRow from '../../../components/FormRow';
import ColorListEditor from '../ColorListEditor';
import type { ColorRow } from '../colorRows';
import InclusionListEditor from '../InclusionListEditor';
import type { InclusionRow } from '../inclusionRows';
import FormSectionBox from '../../../components/FormSectionBox';
import type { DescriptionFormValues } from './descriptionFormValues';

/**
 * The Bodenmerkmale: Bodenart, Farben, Beimengungen and how the description is
 * bounded.
 *
 * Labelled from the PLANUM catalog under both forms — one soil vocabulary, so
 * the two cannot drift. On a Profil the section only shows for a Befund that
 * never appeared in plan.
 *
 * Bodenart and Abgrenzungskriterium are MULTI-valued (comma-joined in one
 * field, as befundator and ExcaBook read them).
 */
export default function SoilSection({
    binding,
    titlePrefix,
    colors,
    onColorsChange,
    inclusions,
    onInclusionsChange
}: {
    binding: FormFieldBinding<DescriptionFormValues>;
    titlePrefix: string;
    colors: ColorRow[];
    onColorsChange: (rows: ColorRow[]) => void;
    inclusions: InclusionRow[];
    onInclusionsChange: (rows: InclusionRow[]) => void;
}) {
    const { suggest, multiSelectJoined } = bindFormFields(binding);
    return (
        <FormSectionBox titlePrefix={titlePrefix} id="soil">
            {multiSelectJoined('soilType', SOIL_TYPE_OPTIONS)}
            <ColorListEditor rows={colors} onChange={onColorsChange} />
            <Divider />
            <InclusionListEditor
                rows={inclusions}
                onChange={onInclusionsChange}
            />
            <Divider />
            <FormRow>
                {suggest('boundaryClarity', BOUNDARY_CLARITY_OPTIONS)}
                {/* Closed list: ExcaBook takes only its own criteria here. */}
                {multiSelectJoined('boundaryBasis', BOUNDARY_BASIS_OPTIONS, {
                    freeSolo: false
                })}
            </FormRow>
            <FormRow>
                {suggest('consistency', CONSISTENCY_OPTIONS)}
                {suggest('contour', CONTOUR_OPTIONS)}
                {suggest('humusContent', HUMUS_CONTENT_OPTIONS)}
            </FormRow>
        </FormSectionBox>
    );
}
